import React, { useState, useCallback, useEffect } from 'react';
import TagManager from 'react-gtm-module';
import { useSelector } from 'react-redux';
import { toaster } from 'evergreen-ui';
import api from '../../../services/api';
import colors from '../../../config/colors.json';
import ImageCropModal from '../../../components/Campanhas/ImageCropModal';
import SectionTitle from '../../../components/Campanhas/SectionTitle';
import Card from '../../../components/Campanhas/Card';
import Input from '../../../components/Campanhas/InputGroup/Input';
import Label from '../../../components/Campanhas/InputGroup/Label';
import ImageUpload from '../../../components/Campanhas/InputGroup/ImageUpload';
import RegisteredLogos from '../../../components/Campanhas/StoreLogo';
import Alert from '../../../components/Campanhas/Alert';
import { CampaignHeaderWithLinks } from '../../../components/Campanhas/CampaignHeader';

const Logo = () => {
    const [images, setImages] = useState([]);
    const [customerId, setCustomerId] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [currentImage, setCurrentImage] = useState('');
    const [currentImageId, setCurrentImageId] = useState(0);
    const [logoName, setLogoName] = useState('');
    const [showAlert, setShowAlert] = useState(false);
    const [alertMsg, setAlertMsg] = useState('');
    const [dropzoneClass, setDropzoneClass] = useState('');
    const [dropzoneErrorMsg, setDropzoneErrorMsg] = useState('');
    const [uploading, setUploading] = useState(false);
    const [idLogo, setIdLogo] = useState(null);
    const userData = useSelector(state => state.user);


	useEffect(() => {
        TagManager.dataLayer({
            dataLayer: {
                event: 'PageView',
                page: 'Gerenciador de logos',
            },
            dataLayerName: 'Campanhas',
        });
    }, []);

    useEffect(() => {
        if (showAlert === true) {
            setTimeout(() => setShowAlert(false), 5000);
        }
    }, [showAlert]);

    const fetchLogos = () => {
        api.get(`/api/v2/customers/${customerId}/logos`)
            .then((response) => {
                setImages(response.data.data);
            });
    };

    const submitFile = (file, name) => {
        const data = new FormData();
        data.append('file', file);
        api.post('/api/v2/files', data)
        .then((response) => {
            setCurrentImageId(response.data.data.id);
            api.post(`/api/v2/customers/${customerId}/logos`, {
                name,
                file_id: response.data.data.id,
            })
            .then((responseLogo) => {
                setIdLogo(responseLogo.data.data.id);
                fetchLogos();
                setModalOpen(true);
            })
            .catch(() => {
                toaster.danger('Erro ao cadastrar logo.');
            });
        })
        .catch(() => {
            toaster.danger('Erro ao enviar arquivo.');
        });
    };

    const submitCrop = (cropedData) => {
        api.post(`/api/v2/logos/${idLogo}/crops`, cropedData)
            .then(() => {
                setAlertMsg('Logo cadastrado com sucesso.');
                setShowAlert(true);
            })
            .catch(() => {
                toaster.danger('Erro ao cadastrar logo.');
            });
    };

    const deleteLogo = (logoId) => {
        api.delete(`/api/v2/customers/${customerId}/logos/${logoId}`)
            .then(() => {
                setAlertMsg('Logo removida com sucesso.');
                setShowAlert(true);
                fetchLogos();
            })
            .catch(() => {
                toaster.danger('Erro ao deletar logo.');
            });
    };

    useEffect(() => {
        if (userData.data) {
            setCustomerId(userData.data.relationships.customer.id_gearbox);
        }
    }, [userData]);

    useEffect(() => {
        fetchLogos();
    }, [customerId]);

    const onDrop = useCallback((acceptedFiles) => {
        const validTypes = ['image/png', 'image/jpg', 'image/jpeg'];
        setUploading(true);

        if (acceptedFiles.length > 1) {
            setDropzoneClass('is-invalid');
            setDropzoneErrorMsg('Ops! Só é permitida uma imagem por vez.');
            setUploading(false);
        } else if (!validTypes.includes(acceptedFiles[0].type)) {
            setDropzoneClass('is-invalid');
            setDropzoneErrorMsg('Ops! Tipo de arquivo inválido.');
            setUploading(false);
        } else if (images.length >= 6) {
            setDropzoneClass('is-invalid');
            setDropzoneErrorMsg('Ops! O limite é de 6 logos.');
            setUploading(false);
        } else {
            setDropzoneClass('');
            acceptedFiles.map((file) => {
                const reader = new FileReader();
                reader.onload = e => setCurrentImage(e.target.result);
                reader.readAsDataURL(file);
                setModalOpen(true);
                submitFile(file, logoName);
                return file;
            });
            setUploading(false);
        }
    }, [images, logoName]);

    return (
	<>
		<div style={{ padding: '30px 38px 0' }}>
			<CampaignHeaderWithLinks>Cadastro de logos</CampaignHeaderWithLinks>
			<ImageCropModal
				setLogoName={setLogoName}
				imageId={currentImageId}
				submitCrop={submitCrop}
				image={currentImage}
				isOpen={modalOpen}
				openAction={setModalOpen}
			/>
			<div className="row">
				<div className="col-12">
					{
                        showAlert && (
                            <Alert>{alertMsg}</Alert>
                        )
                    }
				</div>
			</div>
			<div className="row">
				<div className="col-6">
					<Card
						height="100%"
						padding="32px"
					>
						<div className="row">
							<div className="col-12">
								<SectionTitle
									title="Cadastre seus logos"
									titleSize="16px"
									titleHeight="30px"
									titleMarginBottom="5px"
									subtitle="Seu logo é seu cartão de visitas. É através dele que seus clientes irão te reconhecer. Por isso, é muito importante que você faça suas campanhas com um logo em altíssima qualidade."
									subtitleSize="15px"
									subtitleHeight="15px"
								/>
							</div>
						</div>
						<div className="row" style={{ marginBottom: '21px' }}>
							<div className="col-12">
								<Label
									color={colors.darkGrey}
									fontWeight="500"
									fontSize="15px"
									lineHeight="23px"
								>
									Nome do logo:
								</Label>
								<Input
									type="text"
									placeholder="Dê um nome para o logo"
									id="logo"
									borderradius="8px"
									padding="12px 16px"
									value={logoName}
									onChange={
                                    e => setLogoName(
                                        e.target.value.length > 30
                                        ? e.target.value.substring(0, 30) : e.target.value,
                                    )
                                }
								/>
							</div>
						</div>
						<div className="row">
							<div className="col-12">
								<Label
									color={colors.darkGrey}
									fontWeight="500"
									fontSize="15px"
									lineHeight="23px"
								>
									Faça o Upload do logo:
								</Label>
								{
                                    dropzoneClass === 'is-invalid'
                                    && (
                                        <div style={
                                                {
                                                    color: '#FF0040',
                                                    fontSize: '13px',
                                                    margin: '-10px 0px 5px 0px',
                                                }
                                            }
                                        > {dropzoneErrorMsg}
                                        </div>
                                    )
                                }
								<ImageUpload
									dropzoneClass={dropzoneClass}
									maxFiles={1}
									onDrop={onDrop}
									accept="image/*"
									uploading={uploading}
									buttonStyle="red2"
								/>
							</div>
						</div>
					</Card>
				</div>
				<div className="col-6">
					<Card
						padding="32px"
						height="100%"
					>
						<RegisteredLogos
							images={images}
							deleteLogo={deleteLogo}
						/>
					</Card>
				</div>
			</div>
		</div>
	</>
    );
};

export default Logo;
